import { canv } from "@/canv/global";
import { createWidgetsDumpsBlobFromString } from "@/canv/utils";
import { Group } from "@/group";
import type {
    ClipboardLine,
    ClipboardLineItem,
    ClipboardProcessors,
    ProcessorResult,
} from "./types";

type ClipboardNode = {
    tagName: string;
    items: ClipboardNode[];
    text?: string;
    styles: {
        fontColor: string;
        fontBold: boolean;
        backgroundColor: string;
        fontStrike: boolean;
    };
};

export const clipboardDataToText = async (clipboardItem: ClipboardItem) => {
    const blob = await clipboardItem.getType("text/plain");
    const messageStr = await new Response(blob).text();

    return messageStr;
};

export const clipboardDataToImage = async (clipboardItem: ClipboardItem, type: string) => {
    return clipboardItem.getType(type);
};

export const clipboardHtmlDataToJson = async (clipboardItem: ClipboardItem) => {
    const blob = await clipboardItem.getType("text/html");
    const blobText = await blob.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(blobText, "text/html");
    const arr = htmlToJSON(doc.body);

    return arrToLines(arr);
};

export const clipboardHtmlDataToText = async (clipboardItem: ClipboardItem) => {
    const blob = await clipboardItem.getType("text/html");
    return blob.text();
};

const arrToLines = (arr: ClipboardNode[] = []): ClipboardLine[] => {
    const lines: ClipboardLine[] = [];

    const parseBulletedList = (node: ClipboardNode) => {
        const handler = (childNode: ClipboardNode) => {
            if (childNode.text) {
                lines[lines.length - 1].items.push(...getItemsByNode(childNode));
            } else if (childNode.items.length) {
                for (let i = 0; i < childNode.items.length; i++) {
                    handler(childNode.items[i]);
                }
            }
        };

        for (let i = 0; i < node.items.length; i++) {
            lines.push({
                bullettedList: true,
                align: "left",
                items: [],
                isEmpty: false,
                finished: false,
            });
            handler(node.items[i]);
        }
    };

    const parseJsonToLines = (node: ClipboardNode) => {
        if (node.text) {
            const textLines = node.text.split("\n");
            if (textLines.length === 1) {
                const items = getItemsByNode({ ...node, text: textLines[0] });
                if (
                    lines[lines.length - 1] &&
                    !lines[lines.length - 1].isEmpty &&
                    !lines[lines.length - 1].finished
                ) {
                    lines[lines.length - 1].items.push(...items);
                } else {
                    lines.push({
                        bullettedList: false,
                        align: "left",
                        items,
                        isEmpty: false,
                        finished: false,
                    });
                }
            } else {
                for (let i = 0; i < textLines.length; i++) {
                    const items = getItemsByNode({ ...node, text: textLines[i] });
                    lines.push({
                        bullettedList: false,
                        align: "left",
                        items,
                        isEmpty: false,
                        finished: false,
                    });
                }
            }
        } else if (node.items.length > 0) {
            if (isBullettedList(node.tagName)) {
                return parseBulletedList(node);
            }
            if (
                isBlockTag(node.tagName) &&
                (!lines[lines.length - 1] || lines[lines.length - 1].finished)
            ) {
                lines.push({
                    bullettedList: false,
                    align: "left",
                    items: [],
                    isEmpty: false,
                    finished: false,
                });
            }
            for (let i = 0; i < node.items.length; i++) {
                const item = node.items[i];
                if (isBlockTag(item.tagName) && i !== 0) {
                    lines[lines.length - 1].finished = true;
                }
                parseJsonToLines(item);
            }
            if (isBlockTag(node.tagName)) {
                lines[lines.length - 1].finished = true;
            }
        } else if (
            (isBr(node.tagName) && lines[lines.length - 1]) ||
            (isParagraph(node.tagName) && !node.items.length && !node.text)
        ) {
            if (lines[lines.length - 1].finished || lines[lines.length - 1].isEmpty) {
                lines.push({
                    bullettedList: false,
                    align: "left",
                    items: [],
                    isEmpty: true,
                    finished: false,
                });
            } else {
                lines[lines.length - 1].finished = true;
            }
        }
    };

    for (let i = 0; i < arr.length; i++) {
        parseJsonToLines(arr[i]);
    }
    return lines;
};

const htmlToJSON = (node: HTMLElement): ClipboardNode[] => {
    const result: ClipboardNode[] = [];
    for (let i = 0; i < node.childNodes.length; i++) {
        const elem = node.childNodes[i] as HTMLElement;
        const tagName = (elem.tagName || "span").toLowerCase();
        const fontColor =
            (node as HTMLElement).style?.color &&
            !["inherit", "initial"].includes((node as HTMLElement).style.color)
                ? (node as HTMLElement).style.color
                : "";
        const fontBold =
            (node as HTMLElement).style?.fontWeight === "bold" ||
            (!Number.isNaN(Number((node as HTMLElement).style?.fontWeight)) &&
                Number((node as HTMLElement).style?.fontWeight) > 400) ||
            ["strong", "b"].includes(tagName) ||
            false;
        const backgroundColor =
            (node as HTMLElement).style?.backgroundColor &&
            !["inherit", "initial"].includes((node as HTMLElement).style.backgroundColor)
                ? (node as HTMLElement).style.backgroundColor
                : "";
        const fontStrike = (node as HTMLElement).style?.textDecoration === "line-through" || false;
        const styles = { fontColor, fontBold, backgroundColor, fontStrike };
        if (isBr(tagName)) {
            result.push({ tagName, items: [], styles });
        } else if (
            isParagraph(tagName) &&
            !elem.textContent &&
            !(elem as HTMLElement).children.length
        ) {
            result.push({ tagName, items: [], styles });
        } else if (["img", "button", "svg"].includes(tagName)) {
            result.push({ tagName, items: [], styles });
        } else if (elem.childNodes.length) {
            if (isBlockTag(tagName)) {
                result.push({ tagName, items: htmlToJSON(elem), styles });
            } else {
                result.push(...htmlToJSON(elem));
            }
        } else if (elem.textContent) {
            result.push({ tagName, items: [], text: elem.textContent, styles });
        }
    }
    return result;
};

const getItemsByNode = (node: ClipboardNode): ClipboardLineItem[] => {
    const items: ClipboardLineItem[] = [];
    if (!node.text) return items;
    for (let i = 0; i < node.text.length; i++) {
        items.push({
            value: node.text[i],
            fontColor: node.styles.fontColor,
            fontBold: node.styles.fontBold ? "bold" : "",
            backgroundColor: node.styles.backgroundColor,
            fontStrike: node.styles.fontStrike,
        });
    }
    return items;
};

const isBullettedList = (tagName) => {
    const lowerTagName = tagName.toLowerCase();

    return lowerTagName === "ul" || lowerTagName === "ol";
};

const isBlockTag = (tagName) => {
    if (!tagName) return false;

    const lowerTagName = tagName.toLowerCase();
    const blockTags = [
        "div",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "p",
        "ul",
        "ol",
        "li",
        "header",
        "footer",
        "section",
        "article",
        "blockquote",
        "table",
        "form",
        "nav",
    ];

    return blockTags.includes(lowerTagName);
};

const isParagraph = (tagName: string) => {
    if (!tagName) return false;

    return tagName.toLowerCase() === "p";
};

const isBr = (tagName: string) => {
    if (!tagName) return false;

    return tagName.toLowerCase() === "br";
};

let jespoDumpsClipboardContents: ClipboardItems | undefined;

export async function checkIsJespoDumps(): Promise<boolean> {
    let clipboardContents: ClipboardItem[] | undefined;
    try {
        clipboardContents = await navigator.clipboard.read();
    } catch (e) {
        // сюда попадем если скопировать пустой текст ""
        // clipboardContents == undefined. for ниже просто не запустится
    }

    const textItemProcessor = async (messageStr: string): Promise<boolean> => {
        let ifJson = false;
        let jsonOb: unknown = null;
        try {
            jsonOb = JSON.parse(messageStr);
            ifJson = true;
        } catch (e) {
            return false;
        }
        return (
            ifJson &&
            jsonOb != null &&
            typeof jsonOb === "object" &&
            jsonOb !== null &&
            "isJespoDumps" in jsonOb
        );
    };

    let isJespoDumps = false;

    if (!clipboardContents) return false;

    for (const clipboardItem of clipboardContents) {
        if (clipboardItem.types.includes("text/plain")) {
            const res = await clipboardDataToText(clipboardItem).then(textItemProcessor);
            if (res) {
                isJespoDumps = true;
            } else {
                isJespoDumps = false;
                break;
            }
        } else {
            isJespoDumps = false;
            break;
        }
    }

    jespoDumpsClipboardContents = isJespoDumps ? clipboardContents : undefined;

    return isJespoDumps;
}

export async function saveTextToClipboard(text: string) {
    if (navigator.clipboard?.writeText) {
        try {
            return await navigator.clipboard.writeText(text);
        } catch (e) {
            console.warn("Modern API failed, using fallback", e);
        }
    }

    try {
        if (window.chrome) {
            saveTextToClipboardChromium(text);
        } else {
            saveTextToClipboardSafari(text);
        }
    } catch (e) {
        canv.captureException(e);
    }
}

async function saveTextToClipboardChromium(containerString: string) {
    if (navigator.permissions !== undefined && navigator.permissions.query !== undefined) {
        try {
            await navigator.permissions.query({
                name: "clipboard-write" as PermissionName,
            });
        } catch (e) {
            // хочется тут убрать return. Выше есть try.
            // но пока нету причин так делать.
            return;
        }
    }

    navigator.clipboard.write([
        new ClipboardItem({
            "text/plain": createWidgetsDumpsBlobFromString(containerString),
        }),
    ]);
}

export function saveTextToClipboardSafari(containerString: string) {
    if (
        typeof ClipboardItem === "undefined" ||
        navigator.clipboard === undefined ||
        navigator.clipboard.write === undefined
    ) {
        return;
    }

    const getter = async () => {
        if (navigator.permissions !== undefined && navigator.permissions.query !== undefined) {
            try {
                await navigator.permissions.query({
                    name: "clipboard-write" as PermissionName,
                });
            } catch (e) {
                // в аналогичной функции в textWidget в safari return в этом месте мешал. Без него clipboard работал
                // Есть try выше.
            }
        }

        return createWidgetsDumpsBlobFromString(containerString);
    };

    navigator.clipboard.write([
        new ClipboardItem({
            "text/plain": getter(),
        }),
    ]);
}

export async function copyProccessorExec(copiedBranches: unknown[]) {
    const container = {
        isJespoDumps: true,
        sourceBoardUid: canv.getInitDataProp("boardUID"),
        sourceBoardAccessToken: canv.getInitDataProp("boardAccessToken"),
        dumps: copiedBranches,
    };

    return saveTextToClipboard(JSON.stringify(container));
}

const processClipboardData = async ({
    textItemProcessor,
    imageItemProcessor,
    htmlItemProcessor,
    htmlTextItemProcessor,
}: ClipboardProcessors) => {
    try {
        if (
            navigator === undefined ||
            navigator.clipboard === undefined ||
            navigator.clipboard.read === undefined
        )
            return;

        const cachedClipboardContents = jespoDumpsClipboardContents;
        jespoDumpsClipboardContents = undefined;

        if (
            !cachedClipboardContents &&
            navigator.permissions !== undefined &&
            navigator.permissions.query !== undefined
        ) {
            try {
                await navigator.permissions.query({
                    name: "clipboard-read" as PermissionName,
                });
            } catch (e) {}
        }

        let clipboardContents: ClipboardItems | undefined = cachedClipboardContents;

        if (!clipboardContents) {
            try {
                clipboardContents = await navigator.clipboard.read();
            } catch (e) {
                // сюда попадем если скопировать пустой текст ""
                // clipboardContents == undefined. for ниже просто не запустится
            }
        }

        const groups: Group[] = [];
        let lastProcessorResult: unknown = null;

        const pushGroupsFromResult = (result: unknown) => {
            if (result instanceof Group) {
                groups.push(result);
                return;
            }

            if (Array.isArray(result)) {
                for (const item of result) {
                    if (item instanceof Group) {
                        groups.push(item);
                    }
                }
            }
        };

        if (clipboardContents) {
            for (const clipboardItem of clipboardContents) {
                let processorResult: unknown;

                if (clipboardItem.types.includes("image/png")) {
                    if (imageItemProcessor) {
                        processorResult = await clipboardDataToImage(
                            clipboardItem,
                            "image/png",
                        ).then(imageItemProcessor);
                    }
                } else if (clipboardItem.types.includes("image/jpeg")) {
                    if (imageItemProcessor) {
                        processorResult = await clipboardDataToImage(
                            clipboardItem,
                            "image/jpeg",
                        ).then(imageItemProcessor);
                    }
                } else if (clipboardItem.types.includes("image/gif")) {
                    if (imageItemProcessor) {
                        processorResult = await clipboardDataToImage(
                            clipboardItem,
                            "image/gif",
                        ).then(imageItemProcessor);
                    }
                } else if (clipboardItem.types.includes("image/svg+xml")) {
                    if (imageItemProcessor) {
                        processorResult = await clipboardDataToImage(
                            clipboardItem,
                            "image/svg+xml",
                        ).then(imageItemProcessor);
                    }
                } else if (clipboardItem.types.includes("image/svg")) {
                    if (imageItemProcessor) {
                        processorResult = await clipboardDataToImage(
                            clipboardItem,
                            "image/svg",
                        ).then(imageItemProcessor);
                    }
                } else if (clipboardItem.types.includes("text/html")) {
                    if (htmlTextItemProcessor) {
                        processorResult =
                            await clipboardHtmlDataToText(clipboardItem).then(
                                htmlTextItemProcessor,
                            );
                    } else if (htmlItemProcessor) {
                        processorResult =
                            await clipboardHtmlDataToJson(clipboardItem).then(htmlItemProcessor);
                    }
                    lastProcessorResult = processorResult;
                } else if (clipboardItem.types.includes("text/plain")) {
                    if (textItemProcessor) {
                        processorResult =
                            await clipboardDataToText(clipboardItem).then(textItemProcessor);
                        lastProcessorResult = processorResult;
                    }
                }

                pushGroupsFromResult(processorResult);
            }
        }

        canv.redraw();
        return { groups, lastProcessorResult };
    } catch (e) {
        canv.captureException(e);
    }
};

export async function processDataFromClipboard({
    textItemProcessor,
    imageItemProcessor,
    htmlItemProcessor,
}: {
    textItemProcessor?: (value: string) => ProcessorResult;
    imageItemProcessor?: (blob: Blob) => ProcessorResult;
    htmlItemProcessor?: (value: ClipboardLine[]) => ProcessorResult;
}) {
    return processClipboardData({
        textItemProcessor,
        imageItemProcessor,
        htmlItemProcessor,
    });
}

export async function processDataFromClipboardRawHtml({
    textItemProcessor,
    imageItemProcessor,
    htmlTextItemProcessor,
}: {
    textItemProcessor?: (value: string) => ProcessorResult;
    imageItemProcessor?: (blob: Blob) => ProcessorResult;
    htmlTextItemProcessor?: (value: string) => ProcessorResult;
}) {
    return processClipboardData({
        textItemProcessor,
        imageItemProcessor,
        htmlTextItemProcessor,
    });
}
